/* eslint-disable @typescript-eslint/no-explicit-any */
import Modal from "../../Components/Modal";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  TextField,
  CircularProgress,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import SaveIcon from "@mui/icons-material/Save";
import { UrlEnum } from "../../Utils/Utils";
import { postData } from "../../Services/postData";
import { toast } from "react-toastify";
import { updateData } from "../../Services/updateData";
import theme from "../../Theme/Theme";

type PaymentTypeProps = {
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
  data: any;
};

export type PaymentTypeState = {
  _id: string | null;
  name: string;
  enabled: boolean;
  label: string;
  description: string;
  color: string;
  tierAmounts: number[];
  sortOrder: number;
};

/**
 * Fixed price bands a device's base estimate falls into. Not editable —
 * only the RON amount each payment type adds per band is. Must stay in
 * sync with the same bands in yzzy-frontend-client-questionnaires'
 * DeviceQuestionnaire.tsx (calculatePrice) and the backend's tierAmounts
 * comment in paymentType.model.ts.
 */
const PRICE_TIER_BANDS = [
  "500 - 1000 RON",
  "1000 - 2000 RON",
  "2000 - 3000 RON",
  "3000 - 4000 RON",
  "peste 4000 RON",
];

const defaultState: PaymentTypeState = {
  _id: null,
  name: "",
  enabled: true,
  label: "",
  description: "",
  color: "#358800",
  tierAmounts: [0, 0, 0, 0, 0],
  sortOrder: 0,
};

export default function PaymentType({ open, onClose, data }: PaymentTypeProps) {
  const [state, setState] = useState<PaymentTypeState>(defaultState);
  const [loading, setLoading] = useState(false);
  const notifySuccess = (message: string) => toast.success(message);
  const notifyError = (message: string) => toast.error(message);

  useEffect(() => {
    if (data) {
      setState({
        ...defaultState,
        ...data,
        tierAmounts:
          Array.isArray(data.tierAmounts) && data.tierAmounts.length === 5
            ? data.tierAmounts
            : defaultState.tierAmounts,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setTierAmount(index: number, value: string) {
    const tierAmounts = state.tierAmounts.slice();
    tierAmounts[index] = isNaN(+value) ? 0 : +value;
    setState({ ...state, tierAmounts });
  }

  /**
   * save payment type
   */
  function savePaymentType() {
    if (!state.name.trim()) {
      notifyError(Vocabulary.requiredField);
      return;
    }
    setLoading(true);
    let url = UrlEnum.paymentTypes;
    const body = {
      name: state.name,
      enabled: state.enabled,
      label: state.label,
      description: state.description,
      color: state.color,
      tierAmounts: state.tierAmounts,
      sortOrder: state.sortOrder,
    };
    if (state._id) {
      url = `${url}/${state._id}`;
      updateData(url, body)
        .then(() => {
          onClose(true);
          notifySuccess(Vocabulary.saveSuccess);
          setLoading(false);
        })
        .catch(() => {
          notifyError(Vocabulary.saveError);
          setLoading(false);
        });
    } else {
      postData(url, body)
        .then(() => {
          onClose(true);
          notifySuccess(Vocabulary.saveSuccess);
          setLoading(false);
        })
        .catch(() => {
          notifyError(Vocabulary.saveError);
          setLoading(false);
        });
    }
  }

  return (
    <>
      <Modal
        title={Vocabulary.newPaymentType}
        open={open}
        onClose={() => onClose(false)}
        restModalProps={{
          maxWidth: "md",
        }}
      >
        <br />
        <TextField
          fullWidth
          size="small"
          label={Vocabulary.name}
          value={state.name}
          onChange={(event: any) =>
            setState({ ...state, name: event.target.value })
          }
        />
        <div style={{ marginTop: 20 }}>
          <FormControlLabel
            control={
              <Switch
                checked={state.enabled}
                onChange={(event) =>
                  setState({ ...state, enabled: event.target.checked })
                }
              />
            }
            label={Vocabulary.enabled}
          />
        </div>

        <Typography variant="subtitle1" style={{ marginTop: 20 }}>
          {Vocabulary.paymentTypeCardSection}
        </Typography>
        <TextField
          fullWidth
          size="small"
          style={{ marginTop: 10 }}
          label={Vocabulary.paymentTypeLabel}
          helperText={Vocabulary.paymentTypeLabelHint}
          value={state.label}
          onChange={(event: any) =>
            setState({ ...state, label: event.target.value })
          }
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          style={{ marginTop: 20 }}
          label={Vocabulary.description}
          value={state.description}
          onChange={(event: any) =>
            setState({ ...state, description: event.target.value })
          }
        />
        <Grid container spacing={2} alignItems="center" style={{ marginTop: 4 }}>
          <Grid item xs={8}>
            <TextField
              fullWidth
              size="small"
              label={Vocabulary.paymentTypeColor}
              helperText={Vocabulary.paymentTypeColorHint}
              value={state.color}
              onChange={(event: any) =>
                setState({ ...state, color: event.target.value })
              }
            />
          </Grid>
          <Grid item xs={4}>
            <div
              style={{
                width: "100%",
                height: 40,
                borderRadius: 7,
                backgroundColor: state.color || "#fff",
                border: "1px solid #ccc",
              }}
            />
          </Grid>
        </Grid>
        <TextField
          fullWidth
          size="small"
          type="number"
          style={{ marginTop: 20 }}
          label={Vocabulary.paymentTypeSortOrder}
          helperText={Vocabulary.paymentTypeSortOrderHint}
          value={state.sortOrder}
          onChange={(event: any) =>
            setState({
              ...state,
              sortOrder: isNaN(+event.target.value) ? 0 : +event.target.value,
            })
          }
        />

        <Typography variant="subtitle1" style={{ marginTop: 30 }}>
          {Vocabulary.paymentTypeTiersSection}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
          {Vocabulary.paymentTypeTiersHint}
        </Typography>
        <Grid container spacing={2}>
          {PRICE_TIER_BANDS.map((bandLabel, index) => (
            <Grid item xs={12} sm={6} md={4} key={bandLabel}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={bandLabel}
                value={state.tierAmounts[index]}
                onChange={(event: any) => setTierAmount(index, event.target.value)}
              />
            </Grid>
          ))}
        </Grid>

        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Button
            variant="contained"
            size="large"
            style={{
              color: theme.palette.whiteColor?.main,
              backgroundColor: theme.palette.primary?.main,
            }}
            color="primary"
            onClick={savePaymentType}
          >
            <SaveIcon /> &nbsp;&nbsp;{Vocabulary.save}
          </Button>
        </div>
      </Modal>

      {loading ? (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      ) : (
        ""
      )}
    </>
  );
}
